import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { X, Clock, MapPin, Ban, Loader2, LogOut, Pencil, Check, Tag } from "lucide-react";
import { toast } from "sonner";

export default function StaffProfile({ userId, onClose, onRefresh }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forcingLogout, setForcingLogout] = useState(false);
  const [editingEvv, setEditingEvv] = useState(false);
  const [evvDraft, setEvvDraft] = useState("");
  const [savingEvv, setSavingEvv] = useState(false);

  const load = () => api.get(`/users/${userId}/profile`).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const startEditEvv = () => {
    setEvvDraft(data?.user?.external_staff_id || "");
    setEditingEvv(true);
  };

  const saveEvv = async () => {
    setSavingEvv(true);
    try {
      await api.patch(`/users/${userId}`, { external_staff_id: evvDraft });
      toast.success("EVV Staff ID updated");
      setEditingEvv(false);
      await load();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEvv(false);
    }
  };

  const removeAllFuture = async () => {
    if (!window.confirm("Unassign this staff from ALL upcoming shifts? This is logged in the audit trail.")) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/users/${userId}/remove-future-shifts`);
      toast.success(`Removed from ${r.removed_count} shift(s): ${r.removed.join(", ") || "none"}`);
      await load();
      onRefresh && onRefresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const forceLogout = async () => {
    if (!data?.user) return;
    const name = data.user.name;
    if (!window.confirm(`This will immediately end all active sessions for ${name}. They will need to log in again. Continue?`)) return;
    setForcingLogout(true);
    try {
      await api.post(`/users/${userId}/force-logout`);
      toast.success(`Session ended for ${name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setForcingLogout(false);
    }
  };

  if (!data) return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="accp-card p-8"><Loader2 className="animate-spin"/></div>
    </div>
  );
  const u = data.user;
  const otWarn = data.week_hours >= 40, otSoon = data.week_hours >= 36;

  return (
    <div className="accp-modal-shell items-start md:items-center" onClick={onClose}>
      <div className="accp-modal-card accp-modal-card-xl space-y-5" onClick={(e)=>e.stopPropagation()} data-testid="staff-profile">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="accp-label">{u.code} · {u.role?.toUpperCase()}</div>
            <h2 className="text-3xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>{u.name}</h2>
            <div className="text-sm text-muted-foreground">{u.email} · {u.phone || "—"} · ${u.hourly_rate?.toFixed(2)}/hr</div>
          </div>
          <button onClick={onClose}><X size={20}/></button>
        </div>

        {user?.role === "admin" && (
          <div
            className="rounded-xl border border-border bg-accent/20 p-3"
            data-testid="evv-staff-id-row"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <Tag size={14} className="text-muted-foreground shrink-0"/>
                <div className="accp-label shrink-0">EVV Staff ID</div>
                {/* Status badge — Not linked (⚪) vs Linked (🟢) */}
                {!editingEvv && (
                  u.external_staff_id
                    ? <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-100 text-green-800"
                        data-testid="evv-staff-id-status-linked"
                      >
                        <span className="text-[8px]">🟢</span> Linked
                      </span>
                    : <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600"
                        data-testid="evv-staff-id-status-notlinked"
                      >
                        <span className="text-[8px]">⚪</span> Not linked
                      </span>
                )}
                {!editingEvv && u.external_staff_id && (
                  <div
                    className="text-sm font-mono text-foreground break-all"
                    data-testid="evv-staff-id-value"
                  >
                    {u.external_staff_id}
                  </div>
                )}
              </div>
              {!editingEvv ? (
                <button
                  className="accp-btn-ghost text-xs"
                  onClick={startEditEvv}
                  data-testid="evv-staff-id-edit-btn"
                >
                  <Pencil size={13}/> {u.external_staff_id ? "Edit link" : "Link EVV Staff ID"}
                </button>
              ) : (
                <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                  <input
                    className="accp-input flex-1 font-mono text-xs"
                    placeholder="staff/4fa4c7a7-a0ec-…"
                    value={evvDraft}
                    onChange={(e) => setEvvDraft(e.target.value)}
                    disabled={savingEvv}
                    autoFocus
                    data-testid="evv-staff-id-input"
                  />
                  <button
                    className="accp-btn-primary text-xs"
                    onClick={saveEvv}
                    disabled={savingEvv}
                    data-testid="evv-staff-id-save-btn"
                  >
                    {savingEvv ? <Loader2 size={13} className="animate-spin"/> : <Check size={13}/>} Save
                  </button>
                  <button
                    className="accp-btn-ghost text-xs"
                    onClick={() => setEditingEvv(false)}
                    disabled={savingEvv}
                    data-testid="evv-staff-id-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Linked-metadata line (only when linked) */}
            {!editingEvv && u.external_staff_id && (
              <div
                className="text-[11px] text-muted-foreground mt-2 ml-6 flex flex-wrap gap-x-4 gap-y-1"
                data-testid="evv-staff-id-metadata"
              >
                <span>Provider: <span className="font-medium text-foreground">
                  {(u.external_staff_provider || "md_dda").toUpperCase().replace("_", " ")}
                </span></span>
                {u.external_staff_linked_at && (
                  <span>Linked: <span className="font-medium text-foreground">
                    {new Date(u.external_staff_linked_at).toLocaleString()}
                  </span></span>
                )}
                {u.external_staff_linked_by_name && (
                  <span>By: <span className="font-medium text-foreground">
                    {u.external_staff_linked_by_name}
                  </span></span>
                )}
              </div>
            )}

            <div className="text-[11px] text-muted-foreground mt-2 ml-6">
              This field is only required for staff who receive imported EVV
              visits (e.g. Sandata or other supported EVV providers). Test users
              and non-EVV staff can safely leave this blank.
            </div>
          </div>
        )}

        <div className={`rounded-xl p-4 ${otWarn ? "compliance-expired" : otSoon ? "bg-[#FFF8E6] border border-[#FFE4A0] text-[#7A5A00]" : "bg-accent/40 text-primary"}`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="accp-label">This week</div>
              <div className="text-2xl font-semibold" style={{fontFamily:'Outfit'}}>{data.week_hours} hrs</div>
            </div>
            {otWarn && <span className="text-xs font-bold uppercase tracking-wider">Critical · ≥ 40h</span>}
            {!otWarn && otSoon && <span className="text-xs font-bold uppercase tracking-wider">Warning · ≥ 36h</span>}
          </div>
        </div>

        <div>
          <div className="accp-label mb-2">Credentials</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {data.certifications.map((c) => (
              <div key={c.id} className="rounded-xl border border-border p-3">
                <div className="flex justify-between items-start">
                  <div className="text-xs text-muted-foreground">{c.code}</div>
                  <ComplianceBadge status={c.status}/>
                </div>
                <div className="font-semibold text-primary mt-1">{c.type.replace("_"," ")}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Exp {c.expires_at?.slice(0,10)}</div>
              </div>
            ))}
            {data.certifications.length===0 && <div className="text-sm text-muted-foreground">No credentials recorded.</div>}
          </div>
        </div>

        <div>
          <div className="accp-label mb-2">Upcoming shifts ({data.upcoming_shifts.length})</div>
          {data.upcoming_shifts.length === 0
            ? <div className="text-sm text-muted-foreground">None scheduled.</div>
            : <ul className="space-y-1">
                {data.upcoming_shifts.map((s) => (
                  <li key={s.id} className="text-sm flex items-center justify-between border border-border rounded-xl px-3 py-2">
                    <span className="font-mono text-xs text-muted-foreground">{s.code}</span>
                    <span><Clock size={12} className="inline mr-1"/>{new Date(s.start_time).toLocaleString()}</span>
                    <span className="text-muted-foreground"><MapPin size={12} className="inline mr-1"/>{s.funding_source_code || "—"}</span>
                  </li>
                ))}
              </ul>}
        </div>

        {user.role === "admin" && u.id !== user.id && (
          <div className="pt-4 border-t border-border">
            <div className="accp-label mb-2">Session · Security</div>
            <button
              className="accp-btn-outline w-full"
              onClick={forceLogout} disabled={forcingLogout}
              data-testid="force-logout-btn"
            >
              {forcingLogout ? <Loader2 size={14} className="animate-spin"/> : <LogOut size={14}/>}
              Force logout
            </button>
            <p className="text-[11px] text-muted-foreground mt-2">
              Immediately ends every active session for this user. Their existing JWT is revoked server-side; they can sign in again normally with their current password.
            </p>
          </div>
        )}

        {user.role === "admin" && data.upcoming_shifts.length > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="accp-label mb-2 text-destructive">Safety valve</div>
            <button
              className="accp-btn-outline w-full text-destructive border-destructive/30 hover:bg-destructive/5"
              onClick={removeAllFuture} disabled={busy}
              data-testid="remove-future-shifts"
            >
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Ban size={14}/>}
              Remove from all {data.upcoming_shifts.length} upcoming shift(s)
            </button>
            <p className="text-[11px] text-muted-foreground mt-2">
              This unassigns the staff member from every upcoming shift. Every removal is logged in the append-only audit trail.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
